// TypeScript Interfaces
interface CURPDetails {
  paternalSurname: string;
  maternalSurname: string;
  firstName: string;
  birthDate: string;
  gender: string;
  federativeEntity: string;
  internalConsonants: string;
  homoclave: string;
  checkDigit: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  details: CURPDetails | null;
}

interface DateValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates a Mexican CURP (Clave Única de Registro de Población)
 * @param curp - The CURP to validate
 * @returns Object with validation result and details
 */
function validateCURP(curp: string): ValidationResult {
  const result: ValidationResult = {
    isValid: false,
    errors: [],
    details: null,
  };

  // Check if CURP is provided
  if (!curp) {
    result.errors.push("CURP not provided");
    return result;
  }

  // Convert to uppercase and trim spaces
  const cleanedCURP = curp.toString().toUpperCase().trim();

  // Verify length
  if (cleanedCURP.length !== 18) {
    result.errors.push("CURP must be exactly 18 characters long");
    return result;
  }

  // Regular expression to validate CURP format
  const curpRegex = /^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[0-9A-Z][0-9]$/;

  if (!curpRegex.test(cleanedCURP)) {
    result.errors.push("Invalid CURP format");
    return result;
  }

  // Extract CURP components
  const paternalSurname: string = cleanedCURP.substring(0, 2);
  const maternalSurname: string = cleanedCURP.charAt(2);
  const firstName: string = cleanedCURP.charAt(3);
  const year: string = cleanedCURP.substring(4, 6);
  const month: string = cleanedCURP.substring(6, 8);
  const day: string = cleanedCURP.substring(8, 10);
  const gender: string = cleanedCURP.charAt(10);
  const federativeEntity: string = cleanedCURP.substring(11, 13);
  const internalConsonants: string = cleanedCURP.substring(13, 16);
  const homoclave: string = cleanedCURP.charAt(16);
  const checkDigit: string = cleanedCURP.charAt(17);

  // Validate birthdate
  const birthDateValidation: DateValidationResult = validateBirthDate(
    year,
    month,
    day
  );
  if (!birthDateValidation.isValid) {
    result.errors.push(...birthDateValidation.errors);
  }

  // Validate gender
  if (!["H", "M"].includes(gender)) {
    result.errors.push("Gender must be H (Male) or M (Female)");
  }

  // Validate federative entity
  const federativeEntities: readonly string[] = [
    "AS",
    "BC",
    "BS",
    "CC",
    "CL",
    "CM",
    "CS",
    "CH",
    "DF",
    "DG",
    "GT",
    "GR",
    "HG",
    "JC",
    "MC",
    "MN",
    "MS",
    "NT",
    "NL",
    "OC",
    "PL",
    "QT",
    "QR",
    "SP",
    "SL",
    "SR",
    "TC",
    "TS",
    "TL",
    "VZ",
    "YN",
    "ZS",
    "NE",
  ] as const;

  if (!federativeEntities.includes(federativeEntity)) {
    result.errors.push("Invalid federative entity code");
  }

  // Validate check digit
  const calculatedDigit: string = calculateCheckDigit(
    cleanedCURP.substring(0, 17)
  );
  if (checkDigit !== calculatedDigit) {
    result.errors.push("Incorrect check digit");
  }

  // Check for offensive words (words not allowed)
  const prohibitedWords: readonly string[] = [
    "BACA",
    "BAKA",
    "BUEI",
    "BUEY",
    "CACA",
    "CACO",
    "CAGA",
    "CAGO",
    "CAKA",
    "CAKO",
    "COGE",
    "COGI",
    "COJA",
    "COJE",
    "COJI",
    "COJO",
    "COLA",
    "CULO",
    "FALO",
    "FETO",
    "GETA",
    "GUEI",
    "GUEY",
    "JETA",
    "JOTO",
    "KACA",
    "KACO",
    "KAGA",
    "KAGO",
    "KAKA",
    "KAKO",
    "KOGE",
    "KOGI",
    "KOJA",
    "KOJE",
    "KOJI",
    "KOJO",
    "KOLA",
    "KULO",
    "LILO",
    "LOCA",
    "LOCO",
    "LOKA",
    "LOKO",
    "MAME",
    "MAMO",
    "MEAR",
    "MEAS",
    "MEON",
    "MIAR",
    "MION",
    "MOCO",
    "MOKO",
    "MULA",
    "MULO",
    "NACA",
    "NACO",
    "PEDA",
    "PEDO",
    "PENE",
    "PIPI",
    "PITO",
    "POPO",
    "PUTA",
    "PUTO",
    "QULO",
    "RATA",
    "ROBA",
    "ROBE",
    "ROBO",
    "RUIN",
    "SENO",
    "TETA",
    "VACA",
    "VAGA",
    "VAGO",
    "VAKA",
    "VUEI",
    "VUEY",
    "WUEI",
    "WUEY",
  ] as const;

  const firstCombination: string = cleanedCURP.substring(0, 4);
  if (prohibitedWords.includes(firstCombination)) {
    result.errors.push("Letter combination not allowed (offensive word)");
  }

  // If no errors, it's valid
  if (result.errors.length === 0) {
    // Calculate the correct full year using the same logic as validateBirthDate
    let fullYear: number = parseInt(year);
    const currentYear: number = new Date().getFullYear();
    const minimumAge = 18;
    const cutoffYear: number = currentYear - minimumAge;
    const cutoffTwoDigitYear: number = cutoffYear % 100;

    if (fullYear <= cutoffTwoDigitYear) {
      fullYear += 2000;
    } else {
      fullYear += 1900;
    }

    result.isValid = true;
    result.details = {
      paternalSurname: paternalSurname,
      maternalSurname: maternalSurname,
      firstName: firstName,
      birthDate: `${fullYear}-${month}-${day}`,
      gender: gender === "H" ? "Male" : "Female",
      federativeEntity: federativeEntity,
      internalConsonants: internalConsonants,
      homoclave: homoclave,
      checkDigit: checkDigit,
    };
  }

  return result;
}

/**
 * Validates a birth date extracted from CURP
 * @param year - Year in YY format
 * @param month - Month in MM format
 * @param day - Day in DD format
 * @returns Date validation result
 */
function validateBirthDate(
  year: string,
  month: string,
  day: string
): DateValidationResult {
  const result: DateValidationResult = { isValid: true, errors: [] };

  // Convert 2-digit year to 4-digit year
  let fullYear: number = parseInt(year);
  const currentDate: Date = new Date();
  const currentYear: number = currentDate.getFullYear();
  const minimumAge = 18;
  const cutoffYear: number = currentYear - minimumAge;
  const cutoffTwoDigitYear: number = cutoffYear % 100;

  if (fullYear <= cutoffTwoDigitYear) {
    fullYear += 2000;
  } else {
    fullYear += 1900;
  }

  const monthNum: number = parseInt(month);
  const dayNum: number = parseInt(day);

  // Validate basic ranges
  if (monthNum < 1 || monthNum > 12) {
    result.isValid = false;
    result.errors.push("Invalid month");
    return result;
  }

  if (dayNum < 1 || dayNum > 31) {
    result.isValid = false;
    result.errors.push("Invalid day");
    return result;
  }

  // Create Date object for more precise validation
  const date: Date = new Date(fullYear, monthNum - 1, dayNum);

  if (
    date.getFullYear() !== fullYear ||
    date.getMonth() !== monthNum - 1 ||
    date.getDate() !== dayNum
  ) {
    result.isValid = false;
    result.errors.push("Invalid date (day does not exist in specified month)");
  }

  // Validate that it's not a future date
  if (date > currentDate) {
    result.isValid = false;
    result.errors.push("Birth date cannot be in the future");
  }

  // Validate minimum age requirement
  const age = currentYear - fullYear;
  const hasHadBirthday =
    currentDate.getMonth() > monthNum - 1 ||
    (currentDate.getMonth() === monthNum - 1 &&
      currentDate.getDate() >= dayNum);

  const actualAge = hasHadBirthday ? age : age - 1;

  if (actualAge < minimumAge) {
    result.isValid = false;
    result.errors.push(
      `Person must be at least ${minimumAge} years old to have a CURP`
    );
  }

  return result;
}

/**
 * Calculates the CURP check digit
 * @param curp17 - The first 17 characters of the CURP
 * @returns The calculated check digit
 */
function calculateCheckDigit(curp17: string): string {
  const dictionary = "0123456789ABCDEFGHIJKLMNÑOPQRSTUVWXYZ";
  let sum = 0;

  // Calculate weighted sum for positions 0-16 (weights 18 down to 2)
  for (let i = 0; i < 17; i++) {
    const character: string = curp17.charAt(i);
    const value: number = dictionary.indexOf(character);
    if (value === -1) {
      // If character not found in dictionary, skip or handle error
      continue;
    }
    sum += value * (18 - i);
  }

  // Calculate check digit using modulo 10
  const remainder: number = sum % 10;
  const checkDigit: number = (10 - remainder) % 10;
  return checkDigit.toString();
}

// Helper function for export (optional)
export { validateCURP, CURPDetails, ValidationResult };
